import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faInstagram,
  faLinkedinIn,
} from "@fortawesome/free-brands-svg-icons";
import type { IconDefinition } from "@fortawesome/fontawesome-svg-core";
import { Fragment } from "react";
import { TrackedLink } from "@/components/analytics/TrackedLink";
import { TrackedAnchor } from "@/components/analytics/TrackedAnchor";
import FivestarLogo from "./FivestarLogo";
import { StoreBadges } from "./StoreBadges";
import { MEMBER_LOGIN_URL, CREATOR_LOGIN_URL } from "@/constants/urls";
import { openCookiePreferences } from "@/lib/consent/consent";
import { isAppContext } from "@/lib/appContext";

type SocialItem = {
  icon: IconDefinition;
  label: string;
  href: string;
  ctaId: string;
};

const SOCIAL: SocialItem[] = [
  { icon: faInstagram,  label: "Instagram", href: "https://www.instagram.com/fivestarvids", ctaId: "social_instagram" },
  { icon: faLinkedinIn, label: "LinkedIn",  href: "https://www.linkedin.com/company/fivestar-app/", ctaId: "social_linkedin" },
];

type FooterLink = {
  label: string;
  href?: string;
  isExternal: boolean;
  ctaId: string;
  onClick?: () => void;
};

type FooterColumn = {
  heading: string;
  // Each row holds one or more links rendered inline on a single line,
  // joined by " & ". Most rows are a single link.
  rows: FooterLink[][];
};

const COLUMNS: FooterColumn[] = [
  {
    heading: "Earn Yours",
    rows: [
      [{ label: "Become a Member",  href: "/become-a-member",  isExternal: false, ctaId: "become_a_member" }],
      [{ label: "Become a Creator", href: "/become-a-creator", isExternal: false, ctaId: "become_a_creator" }],
    ],
  },
  {
    heading: "Account",
    rows: [
      [{ label: "Member Login",  href: MEMBER_LOGIN_URL,  isExternal: true, ctaId: "login_member" }],
      [{ label: "Creator Login", href: CREATOR_LOGIN_URL, isExternal: true, ctaId: "login_creator" }],
    ],
  },
  {
    heading: "Legal",
    rows: [
      [
        { label: "Privacy Policy", href: "/privacy", isExternal: false, ctaId: "privacy" },
        { label: "Terms of Use",   href: "/terms",   isExternal: false, ctaId: "terms" },
      ],
      [{ label: "Support", href: "/support", isExternal: false, ctaId: "support" }],
      [{ label: "Delete Your Data", href: "/delete-account", isExternal: false, ctaId: "delete_account" }],
      [{ label: "Cookie Preferences", onClick: openCookiePreferences, isExternal: false, ctaId: "cookie_prefs" }],
    ],
  },
];

const renderFooterLink = (link: FooterLink) => {
  // Plain button intentionally skips TrackedAnchor/TrackedLink — consent UI must work pre-consent when tracking is off
  if (link.onClick) {
    return (
      <button
        type="button"
        onClick={link.onClick}
        className="text-sm text-zinc-400 hover:text-white transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-cyan focus-visible:ring-offset-2 focus-visible:ring-offset-black"
      >
        {link.label}
      </button>
    );
  }
  return link.isExternal ? (
    <TrackedAnchor
      ctaId={link.ctaId}
      ctaLocation="footer"
      href={link.href ?? ""}
      target="_blank"
      rel="noopener noreferrer"
      className="text-sm text-zinc-400 hover:text-white transition-colors"
    >
      {link.label}
    </TrackedAnchor>
  ) : (
    <TrackedLink
      ctaId={link.ctaId}
      ctaLocation="footer"
      to={link.href ?? ""}
      className="text-sm text-zinc-400 hover:text-white transition-colors"
    >
      {link.label}
    </TrackedLink>
  );
};

export default function SiteFooter() {
  const appContext = isAppContext();
  return (
    <footer className="bg-black text-white py-20">
      <div className="max-w-[1400px] mx-auto px-4 md:px-6">
        <div className="grid grid-cols-1 md:grid-cols-[1.5fr_1fr_1fr_1fr] gap-12 text-center md:text-left">
          <div className="flex flex-col items-center md:items-start">
            <FivestarLogo
              variant="horizontal"
              className="h-9 md:h-10 w-auto text-white"
              fill="currentColor"
            />
            <div className="mt-6 flex gap-3">
              {SOCIAL.map(({ icon, label, href, ctaId }) => (
                <TrackedAnchor
                  key={label}
                  ctaId={ctaId}
                  ctaLocation="footer"
                  href={href}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={label}
                  className="w-9 h-9 rounded-full border border-zinc-700 hover:border-white flex items-center justify-center transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-cyan focus-visible:ring-offset-2 focus-visible:ring-offset-black"
                >
                  <FontAwesomeIcon icon={icon} className="w-4 h-4" />
                </TrackedAnchor>
              ))}
            </div>
            {!appContext && (
              <div className="mt-6">
                <StoreBadges location="footer" />
              </div>
            )}
          </div>

          {COLUMNS.map((col) => (
            <div key={col.heading}>
              <h3 className="text-sm font-black italic uppercase tracking-wider text-white">
                {col.heading}
              </h3>
              <ul className="mt-4 space-y-3">
                {col.rows.filter((row) => !appContext || row[0].ctaId !== "cookie_prefs").map((row) => (
                  <li key={row.map((link) => link.label).join("-")}>
                    {row.map((link, i) => (
                      <Fragment key={link.label}>
                        {i > 0 && (
                          <span className="text-sm text-zinc-600"> &amp; </span>
                        )}
                        {renderFooterLink(link)}
                      </Fragment>
                    ))}
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>

        <div className="mt-16 pt-6 border-t border-zinc-800 text-xs text-zinc-500 text-center md:text-left space-y-1">
          <p>{`© ${new Date().getFullYear()} Fivestar LLC`}</p>
          {appContext ? (
            <p>When opened from the Fivestar app, this website uses storage needed for forms, account access, and security. Advertising and analytics tracking are disabled.</p>
          ) : (
            <p>Apple and the Apple logo are trademarks of Apple Inc. Google Play and the Google Play logo are trademarks of Google LLC.</p>
          )}
        </div>
      </div>
    </footer>
  );
}
